// Lucian UX/UI Prompt #160 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#160
// ファネル。幅で「どこで落ちるか」を見せ、段を押すとその段の率を出す（もう一度押すと全体に戻る）。
// 色は一色相の段階（dataviz で検証済み）。到達点の獲得だけ意味の色（緑）にする（ADR 0022）。
// 下の表は同じ数字の表（色やなぞりに頼らず全部の値が読める）。
import React, { useState } from 'react';
import { buildFunnel } from '../../utils/home-viz.mjs';
import './lucian.css';

const STAGE_COLOR = ['var(--lx-ord-1)', 'var(--lx-ord-2)', 'var(--lx-ord-3)', 'var(--lx-ord-4)', 'var(--lx-ord-5)', 'var(--gain)'];
const DARK_TEXT = [false, false, false, true, true, false]; // 濃い段は白文字
const pct = (r) => (r == null ? '-' : `${(r * 100).toFixed(1)}%`);

export default function ConversionFunnel({ metrics, hideAcq = false, topLostReason = null }) {
  const stages = buildFunnel(metrics).filter(s => !(hideAcq && s.key === 'acq'));
  const [sel, setSel] = useState(null);
  const s = sel != null ? stages.find(x => x.key === sel) : null;
  const first = stages[0];
  const acqStage = stages.find(x => x.key === 'acq');

  let status;
  if (!s) {
    status = hideAcq
      ? <>段を押すと、前の段からの率が出ます。</>
      : <>キャッチから獲得まで通算 <b>{pct(first.toAcq)}</b>（{first.count} → {acqStage?.count ?? 0}）。段を押すと詳しく出ます。</>;
  } else if (s.key === 'catch') {
    status = <><b>{s.label} {s.count}</b>。{!hideAcq && <>ここから獲得まで <b>{pct(s.toAcq)}</b>。</>}</>;
  } else {
    const prev = stages[stages.findIndex(x => x.key === s.key) - 1];
    status = (
      <>
        <b>{prev.label} → {s.label}</b> は <b>{pct(s.fromPrev)}</b>
        {s.key === 'acq' ? '（分母は有効着座）' : `（${prev.count} → ${s.count}）`}
        {!hideAcq && s.key !== 'acq' && <>。ここから獲得まで <b>{pct(s.toAcq)}</b></>}
        {s.key === 'acq' && topLostReason && <>。失注理由の1位は <b>{topLostReason.lost_reason}</b>（{topLostReason.cnt}件）</>}
      </>
    );
  }

  return (
    <div>
      <div className="lx-funnel">
        {stages.map((st, i) => (
          <button key={st.key} type="button" className={`lx-funnel__step${DARK_TEXT[i] ? ' is-dark' : ''}`}
            style={{ '--w': `${(st.width * 100).toFixed(1)}%`, '--c': STAGE_COLOR[i] }}
            aria-pressed={sel === st.key} onClick={() => setSel(v => (v === st.key ? null : st.key))}>
            <b>{st.label}</b><span>{st.count}</span>
          </button>
        ))}
      </div>
      <div className="lx-funnel__status" aria-live="polite">{status}</div>
      <table className="lx-table">
        <thead><tr><th>段</th><th>件数</th><th>前の段から</th>{!hideAcq && <th>獲得まで</th>}</tr></thead>
        <tbody>
          {stages.map((st, i) => (
            <tr key={st.key}>
              <td><span className="lx-key" style={{ background: STAGE_COLOR[i] }} />{st.label}</td>
              <td>{st.count}</td>
              <td>{pct(st.fromPrev)}</td>
              {!hideAcq && <td>{st.key === 'acq' ? '—' : pct(st.toAcq)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
